import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import StudentRepository from '../repositories/StudentRepository';
import InventoryRepository from '../repositories/InventoryRepository';
import IncidentRepository from '../repositories/IncidentRepository';
import StudentAuditLogger from '../audit/StudentAuditLogger';
import StudentController from '../controllers/StudentController';
import InventoryController from '../controllers/InventoryController';
import IncidentController from '../controllers/IncidentController';
import NotificationService from '../services/NotificationService';
import TimelineService from '../services/TimelineService';
import AttendanceService from '../services/AttendanceService';
import ReminderService from '../services/ReminderService';
import { useUser } from './UserContext';

const StudentStoreContext = createContext();

export function StudentStoreProvider({ children }) {
  const { user } = useUser();
  const controller = useMemo(() => {
    const repository = new StudentRepository();
    const auditLogger = new StudentAuditLogger();
    return new StudentController({ repository, auditLogger });
  }, []);

  const inventoryController = useMemo(() => {
    const repository = new InventoryRepository();
    const auditLogger = new StudentAuditLogger();
    return new InventoryController({ repository, studentService: controller.studentService, auditLogger });
  }, [controller]);

  const incidentController = useMemo(() => {
    const repository = new IncidentRepository();
    const auditLogger = new StudentAuditLogger();
    return new IncidentController({
      repository,
      studentService: controller.studentService,
      inventoryService: inventoryController.inventoryService,
      auditLogger
    });
  }, [controller, inventoryController]);

  const notificationService = useMemo(() => new NotificationService(), []);
  const reminderService = useMemo(() => new ReminderService(), []);
  const timelineService = useMemo(() => {
    const attendanceService = new AttendanceService();
    return new TimelineService({
      attendanceService,
      incidentService: incidentController.incidentService,
      inventoryService: inventoryController.inventoryService
    });
  }, [incidentController, inventoryController]);

  const [students, setStudents] = useState(controller.listStudents());
  const [inventoryItems, setInventoryItems] = useState(inventoryController.listItems());
  const [incidents, setIncidents] = useState(incidentController.listIncidents());

  useEffect(() => controller.subscribe(() => setStudents(controller.listStudents())), [controller]);
  useEffect(() => inventoryController.subscribe(() => setInventoryItems(inventoryController.listItems())), [inventoryController]);
  useEffect(() => incidentController.subscribe(() => setIncidents(incidentController.listIncidents())), [incidentController]);

  useEffect(() => {
    incidentController.seedFromLegacyReports(controller.listStudents());
  }, [incidentController, controller]);

  useEffect(() => {
    const runJob = () => {
      reminderService.runScheduledInventoryReminders({
        inventoryItems: inventoryController.listItems(),
        inventoryController
      });
    };
    runJob();
    const intervalId = window.setInterval(runJob, 60 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [inventoryController, reminderService]);

  const accessScope = user?.accessScope || 'staff';

  const visibleStudents = useMemo(() => {
    if (accessScope !== 'staff') return students;
    const userName = user?.name || '';
    const programmeNames = new Set((user?.assignedProgrammes || []).map((value) => value.toString()));
    return students.filter((student) => {
      if (student.keyWorker?.name === userName) return true;
      if (student.supportWorkers?.some((worker) => worker.name === userName)) return true;
      if (programmeNames.size > 0) {
        return (student.enrolments || []).some((enrol) => programmeNames.has(enrol.programme));
      }
      return false;
    });
  }, [students, accessScope, user]);

  const visibleStudentIds = useMemo(
    () => new Set(visibleStudents.map((student) => student.id)),
    [visibleStudents]
  );

  const visibleIncidents = useMemo(() => {
    if (accessScope !== 'staff') return incidents;
    const userName = user?.name || '';
    return incidents.filter((incident) => {
      if (incident.assignedKeyWorker === userName) return true;
      const studentIds = (incident.participants || [])
        .filter((participant) => participant.type === 'student')
        .map((participant) => participant.id);
      return studentIds.some((id) => visibleStudentIds.has(id));
    });
  }, [incidents, accessScope, user, visibleStudentIds]);

  const visibleInventoryItems = useMemo(() => {
    if (accessScope === 'inventory' || accessScope === 'admin') return inventoryItems;
    if (accessScope !== 'staff') return inventoryItems;
    const userName = user?.name || '';
    return inventoryItems.filter((item) => {
      const assignment = inventoryController.getActiveAssignment(item);
      if (!assignment) return item.status === 'available';
      if (assignment.assignedToName === userName) return true;
      return visibleStudents.some(
        (student) =>
          student.id === assignment.assignedToId ||
          student.name === assignment.assignedToName
      );
    });
  }, [accessScope, inventoryController, inventoryItems, user, visibleStudents]);

  return (
    <StudentStoreContext.Provider
      value={{
        controller,
        students: visibleStudents,
        inventoryController,
        inventoryItems: visibleInventoryItems,
        incidentController,
        incidents: visibleIncidents,
        notificationService,
        timelineService
      }}
    >
      {children}
    </StudentStoreContext.Provider>
  );
}

export function useStudentStore() {
  return useContext(StudentStoreContext);
}
